import axios from 'axios';
import { SoilSession } from './soilSessionService';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const base = '/api/v1/nutrition';

export interface SoilGroupSetGroup {
  label: string;
  orchardNames: string[];
  defaultOperators: { operatorId: string; name: string }[];
}
export interface SoilGroupSet {
  _id: string;
  name: string;
  groups: SoilGroupSetGroup[];
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
/** The apply/copy dry-run summary. Identical shape for apply and copy — one backend rule set. */
export interface ApplySummary {
  setGroupCount: number;
  createdGroupCount: number;
  skippedGroupCount: number;
  skippedGroupLabels: string[];
  assignedOrchardCount: number;
  unassignedOrchardCount: number;
  unassignedOrchardNames: string[];
  droppedOperatorCount: number;
}
export interface ApplyResult { summary: ApplySummary; session?: SoilSession }

export const soilGroupSetService = {
  async list(): Promise<SoilGroupSet[]> {
    return (await axios.get(`${base}/soil-group-sets`, { headers: authHeaders() })).data;
  },
  async get(id: string): Promise<SoilGroupSet> {
    return (await axios.get(`${base}/soil-group-sets/${id}`, { headers: authHeaders() })).data;
  },
  async create(body: { name: string; groups: SoilGroupSetGroup[] }): Promise<SoilGroupSet> {
    return (await axios.post(`${base}/soil-group-sets`, body, { headers: authHeaders() })).data;
  },
  async update(id: string, body: { name?: string; groups?: SoilGroupSetGroup[] }): Promise<SoilGroupSet> {
    return (await axios.patch(`${base}/soil-group-sets/${id}`, body, { headers: authHeaders() })).data;
  },
  async remove(id: string): Promise<void> {
    await axios.delete(`${base}/soil-group-sets/${id}`, { headers: authHeaders() });
  },
  /**
   * The route suppresses the write only when `dryRun` is strictly `true`
   * (routes/nutrition.js, matching the `confirm === true` convention on cancel).
   * Always send an explicit boolean so a real write is never ambiguous.
   */
  async apply(sessionId: string, groupSetId: string, opts: { dryRun?: boolean } = {}): Promise<ApplyResult> {
    return (await axios.post(`${base}/soil-application-sessions/${sessionId}/apply-group-set`,
      { groupSetId, dryRun: opts.dryRun === true }, { headers: authHeaders() })).data;
  },
  async copyFrom(sessionId: string, fromSessionId: string, opts: { dryRun?: boolean } = {}): Promise<ApplyResult> {
    return (await axios.post(`${base}/soil-application-sessions/${sessionId}/copy-grouping`,
      { fromSessionId, dryRun: opts.dryRun === true }, { headers: authHeaders() })).data;
  },
  async saveAs(sessionId: string, name: string): Promise<SoilGroupSet> {
    return (await axios.post(`${base}/soil-application-sessions/${sessionId}/save-as-group-set`,
      { name }, { headers: authHeaders() })).data;
  },
};
